import { NGI_WIDGETS_IN_DESIGNER_FF_TOKEN } from '@lumapps/widget-base/constants';

(function IIFE() {
    'use strict';

    /////////////////////////////

    function WidgetTitleController(
        $element,
        $scope,
        $state,
        $timeout,
        Content,
        ContentForm,
        Features,
        InitialSettings,
        Translation,
        Widget,
        Utils,
    ) {
        'ngInject';

        var vm = this;

        /////////////////////////////
        //                         //
        //    Private attributes   //
        //                         //
        /////////////////////////////

        /**
         * A DOM element that darkens the rest of the page when editing the content.
         *
         * @type {Element}
         */
        var _appFilter = angular.element('<div/>', {
            class: 'app-filter app-filter--no-transition',
        });

        /////////////////////////////
        //                         //
        //    Public attributes    //
        //                         //
        /////////////////////////////

        /**
         * Whether the widget should be displayed in NGI or not.
         */
        vm.isNGICompatible =
            Utils.isDesignerMode() &&
            Features.hasFeature(NGI_WIDGETS_IN_DESIGNER_FF_TOKEN) &&
            Features.hasFeature('layout-v2');

        /**
         * Indicates if we are editing the content.
         *
         * @type {boolean}
         */
        vm.editingContent = false;

        /////////////////////////////

        /**
         * Services and utilities.
         */
        vm.Content = Content;
        vm.ContentForm = ContentForm;
        vm.Translation = Translation;

        /////////////////////////////
        //                         //
        //    Private functions    //
        //                         //
        /////////////////////////////

        /**
         * Toggle drag and drop.
         */
        function _toggleDragAndDrop() {
            Widget.isDragDisabled(!Widget.isDragDisabled());
            $element.parents('.component-cell').toggleClass('component-cell--hover-disabled', Widget.isDragDisabled());
        }

        /**
         * Edit the content.
         */
        function _editContent() {
            _toggleDragAndDrop();

            vm.editingContent = true;
            var isNotBasicMode = Content.getViewMode() !== 'basic' && !Content.isCurrentDesignerMode('NEW_SIMPLE_WRITER_MODE');

            if (isNotBasicMode) {
                _appFilter.appendTo('.app-content');
            }

            $timeout(function delayFilterDisplay() {
                if (isNotBasicMode) {
                    _appFilter.addClass('app-filter--is-shown');
                }

                $element.find('.widget-title__input').focus();
            }, 100);
        }

        /**
         * Read the content.
         */
        function _readContent() {
            vm.editingContent = false;

            var isNotBasicMode = Content.getViewMode() !== 'basic' && !Content.isCurrentDesignerMode('NEW_SIMPLE_WRITER_MODE');

            if (isNotBasicMode) {
                _appFilter.removeClass('app-filter--is-shown');
            }
            
            $timeout(function delayFilterRemove() {
                if (isNotBasicMode) {
                    _appFilter.remove();
                }

                _toggleDragAndDrop();
            });
        }

        /////////////////////////////
        //                         //
        //     Public functions    //
        //                         //
        /////////////////////////////

        function setAsNonNgiCompatible() {
            vm.isNGICompatible = false;
        }

        function setAsNgiCompatible() {
            const isInDesignerContext = vm.parentCtrl.designerMode();
            // Prevent the switch to true if the required FF are not enabled.
            if (
                isInDesignerContext &&
                Features.hasFeature(NGI_WIDGETS_IN_DESIGNER_FF_TOKEN) &&
                Features.hasFeature('layout-v2')
            ) {
                vm.isNGICompatible = true;
            }
        }

        function saveTextInWidgetProperties(newText) {
            const content = vm.Content.getCurrent();
            content.title = newText;
            vm.ContentForm.setSlugFromTitle(newText[vm.Translation.inputLanguage], vm.Translation.inputLanguage);
            $scope.$apply();
        }

        function shouldDisplayAsNGI() {
            return vm.isNGICompatible;
        }

        /**
         * Edit the content in basic mode.
         */
        function editContentBasic() {
            if (Content.isCurrentDesignerMode('NEW_SIMPLE_WRITER_MODE')) {
                return;
            }

            if (
                Content.getAction() !== 'get' &&
                (Content.isCurrentDesignerMode('NEW_SIMPLE_WRITER_MODE') ||
                    Content.isCurrentDesignerMode('SIMPLE_LEGACY')) &&
                !vm.editingContent &&
                $state.params.contentType !== InitialSettings.CONTENT_TYPES.COMMUNITY
            ) {
                _editContent();
            }
        }

        /**
         * Edit the content in default or simple mode.
         */
        function editContentDefaultOrSimple() {
            var viewMode = Content.getViewMode();

            if (
                Content.getAction() !== 'get' &&
                !vm.editingContent &&
                (viewMode === 'default' || viewMode === 'simple') &&
                $state.params.contentType !== InitialSettings.CONTENT_TYPES.COMMUNITY
            ) {
                _editContent();
            }
        }

        /**
         * Get widget classes.
         *
         * @return {Array} The widget class.
         */
        function getWidgetClass() {
            var widgetClass = [];

            vm.parentCtrl.getWidgetClass(widgetClass);

            widgetClass.push('widget-editable');

            if (vm.isWidgetEmpty()) {
                widgetClass.push('widget--is-empty');
            }

            if (vm.editingContent) {
                widgetClass.push('widget-title--is-editing');
                widgetClass.push('widget-editable--is-editing');
            }

            if ($state.current.name === 'app.front.community') {
                widgetClass.push('widget-title--in-community');
            }

            return widgetClass;
        }

        /**
         * Is widget empty in designer mode.
         *
         * @return {boolean} If the widget is empty or not.
         */
        function isWidgetEmpty() {
            var title = Content.getCurrent().title;

            return (
                !vm.editingContent &&
                (angular.isUndefined(title) || angular.isUndefinedOrEmpty(vm.parentCtrl.getWidgetTranslation(title)))
            );
        }

        /**
         * Is widget hidden in reading mode.
         *
         * @return {boolean} If the widget is hidden or not.
         */
        function isWidgetHidden() {
            vm.parentCtrl.isHidden = false;

            return vm.parentCtrl.isHidden;
        }

        /**
         * Close edit mode.
         */
        function readContent() {
            if (Content.isCurrentDesignerMode('NEW_SIMPLE_WRITER_MODE')) {
                return;
            }

            _readContent();
        }

        /////////////////////////////

        vm.editContentDefaultOrSimple = editContentDefaultOrSimple;
        vm.editContentBasic = editContentBasic;
        vm.getWidgetClass = getWidgetClass;
        vm.isWidgetEmpty = isWidgetEmpty;
        vm.isWidgetHidden = isWidgetHidden;
        vm.shouldDisplayAsNGI = shouldDisplayAsNGI;
        vm.readContent = readContent;
        vm.setAsNonNgiCompatible = setAsNonNgiCompatible;
        vm.setAsNgiCompatible = setAsNgiCompatible;
        vm.saveTextInWidgetProperties = saveTextInWidgetProperties;

        /////////////////////////////
        //                         //
        //          Events         //
        //                         //
        /////////////////////////////

        $scope.$on('contribution-mode', (evt, contributionMode) => {
            if (contributionMode === 'WRITER_MODE') {
                _editContent();
            } else {
                _readContent();
            }
        });

        /////////////////////////////

        /**
         * Initialize the controller.
         */
        function init() {
            if (Content.isCurrentDesignerMode('NEW_SIMPLE_WRITER_MODE')) {
                _editContent();
            }
        }

        /**
         * Set parent controller.
         *
         * @param {Object} parentCtrl The parent controller.
         */
        this.setParentController = function setParentController(parentCtrl) {
            vm.parentCtrl = parentCtrl;

            const isInDesignerContext = vm.parentCtrl.designerMode();

            // If we are not in the designer context or the content is not v2 compatible
            // we fallback to legacy display (we consider unsaved content as v2 compatible).
            // Here we are trying to guess which display will be the correct one in view mode.
            // This is not perfect, but we want to avoid too much logic in the frontend to know if a content is
            // compatible v2 or not (widget types presents on the content + some specific settings on some widgets)
            if (
                !isInDesignerContext ||
                (Content.getAction() !== 'create' && Content.getCurrent()?.template.isV2Compatible === false)
            ) {
                setAsNonNgiCompatible();
            }

            init();
        };
    }

    /////////////////////////////

    function WidgetTitleDirective() {
        'ngInject';

        function link(scope, el, attrs, ctrls) {
            ctrls[0].setParentController(ctrls[1]);
        }

        return {
            bindToController: true,
            controller: WidgetTitleController,
            controllerAs: 'vm',
            link: link,
            replace: true,
            require: ['widgetTitle', '^widget'],
            restrict: 'E',
            scope: {
                widget: '=',
            },
            // eslint-disable-next-line max-len
            templateUrl:
                '/client/front-office/modules/content/modules/widget/modules/widget-title/views/widget-title.html',
        };
    }

    /////////////////////////////

    angular.module('Widgets').directive('widgetTitle', WidgetTitleDirective);
})();
